import { hostWebApp } from './hostWebApp';
import { getNativeIosLinkedUser } from './nativeApiRouting';
import { isExplicitlySignedOut } from './accountSessions';

export type SafetyNotificationDestination = 'bulletin' | 'safety' | 'field';
export interface SafetyNotificationIntent {
  destination: SafetyNotificationDestination;
  type: string;
  companyId: string;
  recipientUserId: string;
  deskTab: string;
  bulletinId?: string;
  actionId?: string;
  submissionId?: string;
  jobMasterId?: string;
}
const key = (destination: SafetyNotificationDestination) => `zia_safety_notification_${destination}`;
const id = (value: unknown) => typeof value === 'string' && value.trim().length <= 200 ? value.trim() : '';
export function getSafetyNotificationUserId(): string {
  if (isExplicitlySignedOut()) return '';
  return String(getNativeIosLinkedUser()?.userId || hostWebApp.getUserId() || '');
}
export function parseSafetyNotificationIntent(raw: Record<string, unknown>): SafetyNotificationIntent | null {
  const type = id(raw.type);
  const companyId = id(raw.companyId);
  const recipientUserId = id(raw.recipientUserId);
  if (!companyId || !recipientUserId) return null;
  const bulletinId = id(raw.bulletinId), actionId = id(raw.actionId);
  const submissionId = id(raw.submissionId), jobMasterId = id(raw.jobMasterId);
  const destination = type === 'worker_bulletin' && bulletinId ? 'bulletin'
    : type === 'safety_desk' && (actionId || submissionId) ? 'safety'
      : type === 'field_desk' && (actionId || jobMasterId) ? 'field' : null;
  if (!destination) return null;
  return { destination, type, companyId, recipientUserId,
    deskTab: destination === 'bulletin' ? 'bulletins' : submissionId ? 'incidents' : actionId ? 'corrective-actions' : 'job-masters',
    ...(bulletinId ? { bulletinId } : {}), ...(actionId ? { actionId } : {}),
    ...(submissionId ? { submissionId } : {}), ...(jobMasterId ? { jobMasterId } : {}),
  };
}
export function clearSafetyNotificationIntent(destination: SafetyNotificationDestination): void {
  try { sessionStorage.removeItem(key(destination)); } catch { /* unavailable storage */ }
}
export function storeSafetyNotificationIntent(raw: Record<string, unknown>): SafetyNotificationIntent | null {
  const intent = parseSafetyNotificationIntent(raw);
  if (!intent) return null;
  const userId = getSafetyNotificationUserId();
  if (userId && userId !== intent.recipientUserId) return null;
  try {
    for (const destination of ['bulletin', 'safety', 'field'] as const) clearSafetyNotificationIntent(destination);
    sessionStorage.setItem(key(intent.destination), JSON.stringify(intent)); return intent;
  }
  catch { return null; }
}
/** Keep a cold tap through sign-in. A different signed-in account cannot consume it. */
export function readSafetyNotificationIntent(destination: SafetyNotificationDestination): SafetyNotificationIntent | null {
  try {
    const raw = sessionStorage.getItem(key(destination));
    if (!raw) return null;
    const intent = parseSafetyNotificationIntent(JSON.parse(raw));
    if (!intent || intent.destination !== destination) { clearSafetyNotificationIntent(destination); return null; }
    const userId = getSafetyNotificationUserId();
    if (!userId) return null;
    if (userId !== intent.recipientUserId) { clearSafetyNotificationIntent(destination); return null; }
    return intent;
  } catch { clearSafetyNotificationIntent(destination); return null; }
}
